import React, { useEffect, useState, useMemo } from "react";
import CONFIG from "Config";
import { EAIClient } from "../../../services/EAIClient";

const VersionInfo = () => {
    const [envData, setEnvData] = useState<any>(null);
    const [gitBranch, setGitBranch] = useState<string | null>(null);
    const [apiStage, setApiStage] = useState<string>("N/A");
    const [migrationVersion, setMigrationVersion] = useState<string>("Unknown");

    const stage = process.env.STAGE;
    const isStageAllowed = stage === "local" || stage === "test";

    const uiStage = stage || "N/A";
    const uiBranch = CONFIG.branch || "N/A";
    const apiBranch = gitBranch || "N/A";

    useEffect(() => {
        if (!isStageAllowed) return;

        const client = new EAIClient();

        const fetchAll = async () => {
            try {
                const [env, branch, stage, migration] = await Promise.all([
                    client.getEnvConfig(),
                    client.getGitBranch(),
                    client.getApiStage(),
                    client.getMigrationVersion(),
                ]);

                setEnvData(env);
                setGitBranch(branch);
                setApiStage(stage);
                setMigrationVersion(migration);
            } catch (err) {
                console.error("Error fetching env info:", err);
                setEnvData({});
                setGitBranch("Error");
                setApiStage("Error");
                setMigrationVersion("Error");
            }
        };

        fetchAll();
    }, [isStageAllowed]);

    const { dbHost, dbName } = useMemo(() => {
        const uri: string = envData?.SQLALCHEMY_DATABASE_URI_DISPLAY || "";
        const host = uri ? uri.substring(0, uri.lastIndexOf("/")) : "N/A";
        const name = uri ? uri.split("/").pop()?.split("?")[0] || "N/A" : "N/A";
        return { dbHost: host, dbName: name };
    }, [envData]);

    const branchInfo = useMemo(() => {
        const branches = [uiBranch, apiBranch];
        const hasMaster = branches.some(branch => branch === "master");
        const hasDevelop = branches.some(branch => branch === "develop");
        
        if (hasMaster) {
            return {
                type: "master" as const,
                message: "WARNING: You are on the MASTER branch!",
                bgColor: "#DF2935",
                textColor: "white"
            };
        }
        
        if (hasDevelop) {
            return {
                type: "develop" as const,
                message: "CAUTION: You are on the DEVELOP branch!",
                bgColor: "#FDCA40",
                textColor: "black"
            };
        }
        
        return {
            type: "safe" as const,
            message: "You are on a SAFE branch.",
            bgColor: "#0471A6",
            textColor: "white"
        };
    }, [uiBranch, apiBranch]);

    const baseStyle = {
        display: "flex",
        fontFamily: "monospace",
        fontSize: "8px",
        textAlign: "left" as const,
        lineHeight: "1.5",
        position: "fixed" as const,
        bottom: 10,
        left: 305,
        padding: "5px",
        zIndex: 9999,
        borderRadius: "8px",
        boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
    };

    const isLoading = !envData || gitBranch === null;

    const renderContent = () => {
        if (isLoading) {
            return "Loading environment info...";
        }

        return (
            <>
                <div>
                    <div>UI STAGE: {uiStage}</div>
                    <div>API STAGE: {apiStage}</div>
                </div>
                <br />
                <div>
                    <div>UI Git Branch: {uiBranch}</div>
                    <div>API Git Branch: {apiBranch}</div>
                    <div>{branchInfo.message}</div>
                </div>
                <br />
                <div>
                    <div>DB HOST: {dbHost}</div>
                    <div>DB Name: {dbName}</div>
                    <div>Migration: {migrationVersion === "Unknown" ? "Loading..." : migrationVersion}</div>
                </div>
            </>
        );
    };
    
    if (!isStageAllowed) return null;

    return (
        <div
            style={{
                ...baseStyle,
                flexDirection: "row",
                gap: "3px",
                background: branchInfo.bgColor,
                color: branchInfo.textColor,
                justifyContent: isLoading ? "center" : "flex-start",
                alignItems: isLoading ? "center" : "flex-start",
            }}
        >
            {renderContent()}
        </div>
    );
};

export default VersionInfo;
